import { createAction } from 'redux-actions';
import { Fund } from 'src/components/Exchange/types';
import { GROSS_AMOUNT, GROSS_UNIT } from 'src/settings/settings';
import { ItemSelection, WithholdingObject } from 'src/types';
import { callApi, getEndpoint } from 'src/utils/api';
import { action } from 'typesafe-actions';
import { SellTransactionActionTypes as act } from './actionTypes';
import { AddressPayload, AmountObj, DistribTypeObj, UnitObj } from './types';

export const updateSellFund = (toUpdate: Partial<Fund>, meta?) => {
  const { agreementID } = toUpdate;
  if (!agreementID) {
    throw new Error(
      'Cannot update fund when no agreementID specified. ' +
        JSON.stringify(toUpdate)
    );
  }
  return action(act.UPDATE_SELL_FUND, toUpdate, meta);
};
export const selectSellFund = (agreementID: string) => {
  return action(act.SELECT_SELL_FUND, { agreementID });
};

export const deselectSellFund = (agreementID: string) => {
  return action(act.DESELECT_SELL_FUND, { agreementID });
};

export const updateSellFundAmount = (
  amountObj: AmountObj,
  meta = GROSS_AMOUNT
) => {
  const { agreementID, amount } = amountObj;
  const fundObj = {
    agreementID,
    [GROSS_AMOUNT]: amount,
  };
  return updateSellFund(fundObj, meta);
};

export const updateSellFundUnit = (unitObj: UnitObj, meta = GROSS_UNIT) => {
  const { agreementID, unit } = unitObj;
  const fundObj = {
    agreementID,
    [GROSS_AMOUNT]: undefined, // must be undefined, Cleave.js errors on null
    [GROSS_UNIT]: unit,
  };
  return updateSellFund(fundObj, meta);
};

export const updateSellFundDistributionReason = (
  distributionReasonObj: DistribTypeObj
) => {
  const { agreementID, distributionReason } = distributionReasonObj;
  const fundObj = {
    agreementID,
    distributionReason,
  };
  return updateSellFund(fundObj);
};

export const updateSellFundDistributionPayee = (distributionPayeeObj) => {
  const { agreementID, payee } = distributionPayeeObj;
  const fundObj = {
    agreementID,
    payee,
  };
  return updateSellFund(fundObj);
};

export const updateSellFundDistributionDisclaimer = (
  distributionDisclaimerObj
) => {
  const { agreementID, distributionPayeeDisclaimerAccepted } =
    distributionDisclaimerObj;
  const fundObj = {
    agreementID,
    distributionPayeeDisclaimerAccepted,
  };
  return updateSellFund(fundObj);
};

export const getSellFundDistributionBeneficiaryAddresses = (
  agreementIDs: string[]
) => {
  return (dispatch: CallableFunction) => {
    const action = createAction(act.DISTRIBUTION_BENEFICIARY_ADDRESS)(
      callApi(
        `${getEndpoint('entityAddress')}?minorIds=${agreementIDs.join(',')}`
      )
    );
    dispatch(action);
  };
};

// dummy function to establish action type
export const __receiveDistroBeneficiary = (
  body: Record<string, AddressPayload>
) => action(act.DISTRIBUTION_BENEFICIARY_ADDRESS_FULFILLED, { body });

export const selectSellMonth = (
  selection: ItemSelection[],
  agreementID: string
) =>
  action(act.UPDATE_MONTH_SELECTION, {
    selection,
    agreementID,
  });

export const selectSellDay = (
  selection: ItemSelection[],
  agreementID: string
) =>
  action(act.UPDATE_DAY_SELECTION, {
    selection,
    agreementID,
  });

export const updateSellUniqueIDs = (
  agreementID,
  electionAgreementUniqueId1,
  electionAgreementUniqueId2
) =>
  action(act.UPDATE_SELL_UNIQUE_IDS, {
    agreementID,
    electionAgreementUniqueId1,
    electionAgreementUniqueId2,
  });

export const updateFederalWithholding = ({
  federalWithholdingApplied,
  federalWithholdingPercent,
  agreementID,
}: WithholdingObject) =>
  action(act.UPDATE_FEDERAL_WITHHOLDING, {
    federalWithholdingApplied,
    federalWithholdingPercent,
    agreementID,
  });

export function updateRMDShouldValidate(rmdShouldValidate) {
  return action(act.UPDATE_RMD_SHOULD_VALIDATE, { rmdShouldValidate });
}

//This function is only used to satify the typescript checking for this file and is not called
//There is a reset action that lives in the shopping cart actions.js file
export const resetPlaceholder = () => action(act.RESET_TRANSACTION);
